import React, { useMemo } from "react";
import { RADAR_PROFILE_AXES } from "../utils/patientCognitiveRadar";

const CHART = {
  size: 320,
  maxRadius: 118,
  labelRadius: 142,
};

const PATIENT_THEMES = {
  a: {
    area: "rgba(0, 99, 199, 0.22)",
    line: "#0063c7",
    point: "#0063c7",
    legendClass: "expedientes-compare-legend-dot--a",
  },
  b: {
    area: "rgba(34, 197, 94, 0.22)",
    line: "#16a34a",
    point: "#16a34a",
    legendClass: "expedientes-compare-legend-dot--b",
  },
};

const toPoint = (center, radius, angle) => ({
  x: center + radius * Math.cos(angle),
  y: center + radius * Math.sin(angle),
});

function RadarSvg({ profile, theme, showLabels = false }) {
  const center = CHART.size / 2;
  const axes = profile?.length ? profile : RADAR_PROFILE_AXES.map((axis) => ({
    ...axis,
    percentage: null,
    hasData: false,
  }));
  const count = axes.length;
  const angleStep = (2 * Math.PI) / count;
  const startAngle = -Math.PI / 2;

  const axisAngles = axes.map((_, index) => startAngle + index * angleStep);

  const valuePoints = axes.map((axis, index) => {
    const value = axis.hasData ? axis.percentage : 0;
    const radius = (value / 100) * CHART.maxRadius;
    return toPoint(center, radius, axisAngles[index]);
  });

  const polygonPoints = valuePoints.map((point) => `${point.x},${point.y}`).join(" ");
  const gridLevels = [25, 50, 75, 100];

  return (
    <svg
      viewBox={`0 0 ${CHART.size} ${CHART.size}`}
      className="expedientes-radar-chart expedientes-radar-chart--compare"
      role="img"
      aria-hidden={!showLabels}
    >
      {gridLevels.map((level) => {
        const radius = (level / 100) * CHART.maxRadius;
        const points = axisAngles
          .map((angle) => {
            const point = toPoint(center, radius, angle);
            return `${point.x},${point.y}`;
          })
          .join(" ");

        return (
          <polygon key={level} points={points} className="expedientes-radar-grid" />
        );
      })}

      {axisAngles.map((angle, index) => {
        const outer = toPoint(center, CHART.maxRadius, angle);
        return (
          <line
            key={`axis-line-${index}`}
            x1={center}
            y1={center}
            x2={outer.x}
            y2={outer.y}
            className="expedientes-radar-axis"
          />
        );
      })}

      <polygon points={polygonPoints} fill={theme.area} stroke="none" />
      <polygon
        points={polygonPoints}
        fill="none"
        stroke={theme.line}
        strokeWidth="2.5"
      />

      {valuePoints.map((point, index) => (
        <circle
          key={axes[index].id}
          cx={point.x}
          cy={point.y}
          r={axes[index].hasData ? 4 : 3}
          fill={axes[index].hasData ? theme.point : "#d1d5db"}
          stroke="#fff"
          strokeWidth="2"
        />
      ))}

      {showLabels &&
        axisAngles.map((angle, index) => {
          const labelPoint = toPoint(center, CHART.labelRadius, angle);
          return (
            <text
              key={`label-${axes[index].id}`}
              x={labelPoint.x}
              y={labelPoint.y}
              className="expedientes-radar-label"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {axes[index].label}
            </text>
          );
        })}
    </svg>
  );
}

export default function CognitiveRadarCompareChart({
  profileA,
  profileB,
  patientNameA,
  patientNameB,
}) {
  const axes = useMemo(
    () => profileA?.length ? profileA : profileB || [],
    [profileA, profileB]
  );

  return (
    <div className="expedientes-radar-compare">
      <div className="expedientes-radar-compare-chart-col">
        <RadarSvg profile={profileA} theme={PATIENT_THEMES.a} showLabels />
        <p className="expedientes-radar-compare-caption">{patientNameA}</p>
      </div>

      <div className="expedientes-radar-compare-legend">
        <div className="expedientes-compare-legend-key">
          <span className="expedientes-compare-legend-dot expedientes-compare-legend-dot--a" />
          <span>{patientNameA}</span>
        </div>
        <div className="expedientes-compare-legend-key">
          <span className="expedientes-compare-legend-dot expedientes-compare-legend-dot--b" />
          <span>{patientNameB}</span>
        </div>
        <ul className="expedientes-radar-compare-values">
          {axes.map((axis) => {
            const valueA = profileA?.find((item) => item.id === axis.id);
            const valueB = profileB?.find((item) => item.id === axis.id);
            return (
              <li key={axis.id}>
                <span className="expedientes-radar-compare-axis">{axis.label}</span>
                <span className="expedientes-radar-compare-score expedientes-radar-compare-score--a">
                  {valueA?.hasData ? `${valueA.percentage}%` : "—"}
                </span>
                <span className="expedientes-radar-compare-score expedientes-radar-compare-score--b">
                  {valueB?.hasData ? `${valueB.percentage}%` : "—"}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="expedientes-radar-compare-chart-col">
        <RadarSvg profile={profileB} theme={PATIENT_THEMES.b} showLabels />
        <p className="expedientes-radar-compare-caption">{patientNameB}</p>
      </div>
    </div>
  );
}
